import { Injectable } from '@angular/core';

import { IObjectStringKeyMap } from '@shared/models';
import { Observation, ObservationTypes } from '@services/observations/observation.interfaces';
import { IProperty, IPropertyMeta, PropertyCategory } from '@services/property/property-model.interfaces';
import {
  Asset,
  HealthEvent,
  ReportAsset,
  StateEvent
} from '@services/assets/asset.interfaces';
import { stateEventsAsHealthEvents } from '@services/property/in-use-state';
import { AssetStateHistoryEntity } from '@modules/observation/pages/asset-status/pages/detail/model/asset-state.interfaces';
import {
  AssetByHealthEvent,
  AssetByObservation,
  AssetByPropertyValue
} from '@modules/reporting/models/types/asset/model';
import {
  AccountsService,
  AssetsService,
  CompareValueService,
  FoldersDataService,
  ObjectsService,
  ObservationService,
  PropertyService,
  TeamsService,
  UserService,
  UtilsService
} from '@services';
import { SubscriberService, Feature } from '@services/subscriber/subscriber.service';
import { TimeDisplayService } from '@services/timeDisplay/time-display.service';
import { WorkScheduleService } from '@services/work-schedule/work-schedule.service';
import { CompareDurationValue } from '@shared/components/compare-duration/model';
import { expandMeasure, Treatment } from '../measures';

import { TranslateService } from '@ngx-translate/core';
import {
  cloneDeep,
  each,
  filter,
  find,
  flatMap,
  flatten,
  get,
  has,
  includes,
  intersection,
  isUndefined,
  join,
  map,
  omit,
  orderBy,
  pick,
  some,
  sumBy,
  values
} from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class AssetSummaryModelService {
  // Cache for work time calculations to avoid redundant computation
  // Key: `${assetID}_${targetHealth}`, Value: { total: number, periodCount: number }
  private workTimeCache = new Map<string, { total: number; periodCount: number }>();

  private columnOptions = [
    {
      id: 'observationsAdded',
      description: this.translate.instant('REPORTING.Observations_Added'),
      label: this.translate.instant('REPORTING.Observations_Added'),
      units: this.translate.instant('REPORTING.Observations_Added'),
      cellType: 'number',
      showWhen: true,
      func: (collection: Asset[]) => this.countObservationEvents(collection, 'created')
    },
    {
      id: 'observationsFixed',
      description: this.translate.instant('REPORTING.Observations_Fixed'),
      label: this.translate.instant('REPORTING.Observations_Fixed'),
      units: this.translate.instant('REPORTING.Observations_Fixed'),
      cellType: 'number',
      showWhen: true,
      func: (collection: Asset[]) => this.countObservationEvents(collection, 'fixed')
    },
    {
      id: 'observationsClosed',
      description: this.translate.instant('REPORTING.Observations_Closed'),
      label: this.translate.instant('REPORTING.Observations_Closed'),
      units: this.translate.instant('REPORTING.Observations_Closed'),
      cellType: 'number',
      showWhen: true,
      func: (collection: Asset[]) => this.countObservationEvents(collection, 'resolved')
    },
    {
      id: 'totalTimeUnavailable',
      description: this.translate.instant('PROPERTY.Total_Time_Unavailable'),
      label: this.translate.instant('PROPERTY.Total_Time_Unavailable'),
      units: this.translate.instant('PROPERTY.Total_Time_Unavailable'),
      cellType: 'string',
      showWhen: true,
      useRenderFunc: true,
      isDuration: true,
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          if (type === 'sort' || type === 'type') return dataOrCollection;
          return this.timeDisplayService.formatDuration(dataOrCollection / 1000);
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getTotalHealthTime(asset.healthEntities, 0) : 0;
      }
    },
    {
      id: 'totalTimeStrained',
      description: this.translate.instant('PROPERTY.Total_Time_Strained'),
      label: this.translate.instant('PROPERTY.Total_Time_Strained'),
      units: this.translate.instant('PROPERTY.Total_Time_Strained'),
      cellType: 'string',
      showWhen: true,
      useRenderFunc: true,
      isDuration: true,
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          if (type === 'sort' || type === 'type') return dataOrCollection;
          return this.timeDisplayService.formatDuration(dataOrCollection / 1000);
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getTotalHealthTime(asset.healthEntities, 1) : 0;
      }
    },
    {
      id: 'totalTimeAvailable',
      description: this.translate.instant('PROPERTY.Total_Time_Available'),
      label: this.translate.instant('PROPERTY.Total_Time_Available'),
      units: this.translate.instant('PROPERTY.Total_Time_Available'),
      cellType: 'string',
      showWhen: true,
      useRenderFunc: true,
      isDuration: true,
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          if (type === 'sort' || type === 'type') return dataOrCollection;
          return this.timeDisplayService.formatDuration(dataOrCollection / 1000);
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getTotalHealthTime(asset.healthEntities, 2) : 0;
      }
    },
    {
      id: 'averageTimeUnavailable',
      description: this.translate.instant('PROPERTY.Average_Time_Unavailable'),
      label: this.translate.instant('PROPERTY.Average_Time_Unavailable'),
      units: this.translate.instant('PROPERTY.Average_Time_Unavailable'),
      cellType: 'string',
      showWhen: true,
      useRenderFunc: true,
      isDuration: true,
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          if (type === 'sort' || type === 'type') return dataOrCollection;
          return this.timeDisplayService.formatDuration(dataOrCollection / 1000);
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getAverageHealthTime(asset.healthEntities, 0) : 0;
      }
    },
    {
      id: 'averageTimeStrained',
      description: this.translate.instant('PROPERTY.Average_Time_Strained'),
      label: this.translate.instant('PROPERTY.Average_Time_Strained'),
      units: this.translate.instant('PROPERTY.Average_Time_Strained'),
      cellType: 'string',
      showWhen: true,
      useRenderFunc: true,
      isDuration: true,
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          if (type === 'sort' || type === 'type') return dataOrCollection;
          return this.timeDisplayService.formatDuration(dataOrCollection / 1000);
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getAverageHealthTime(asset.healthEntities, 1) : 0;
      }
    },
    {
      id: 'averageTimeAvailable',
      description: this.translate.instant('PROPERTY.Average_Time_Available'),
      label: this.translate.instant('PROPERTY.Average_Time_Available'),
      units: this.translate.instant('PROPERTY.Average_Time_Available'),
      cellType: 'string',
      showWhen: true,
      useRenderFunc: true,
      isDuration: true,
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          if (type === 'sort' || type === 'type') return dataOrCollection;
          return this.timeDisplayService.formatDuration(dataOrCollection / 1000);
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getAverageHealthTime(asset.healthEntities, 2) : 0;
      }
    },
    {
      id: 'totalWorkTimeUnavailable',
      description: this.translate.instant('WORK_SCHEDULE.Total_Work_Time_Unavailable'),
      label: this.translate.instant('WORK_SCHEDULE.Total_Work_Time_Unavailable'),
      units: this.translate.instant('WORK_SCHEDULE.Total_Work_Time_Unavailable'),
      cellType: 'string',
      showWhen: () => this.subscriberService.usesFeature(Feature.WORK_SCHEDULE),
      useRenderFunc: true,
      isDuration: true,
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        // Render phase: dataOrCollection is the pre-calculated ms value
        if (typeof dataOrCollection === 'number') {
          if (type === 'sort' || type === 'type') return dataOrCollection;
          return this.utils.secsToDuration(dataOrCollection / 1000, false, 'mm');
        }
        // Data building phase: dataOrCollection is the assets array
        const asset = dataOrCollection?.[0];
        return asset ? this.getTotalWorkTimeFromEvents(asset, 0) : 0;
      }
    },
    {
      id: 'totalWorkTimeStrained',
      description: this.translate.instant('WORK_SCHEDULE.Total_Work_Time_Strained'),
      label: this.translate.instant('WORK_SCHEDULE.Total_Work_Time_Strained'),
      units: this.translate.instant('WORK_SCHEDULE.Total_Work_Time_Strained'),
      cellType: 'string',
      showWhen: () => this.subscriberService.usesFeature(Feature.WORK_SCHEDULE),
      useRenderFunc: true,
      isDuration: true,
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          if (type === 'sort' || type === 'type') return dataOrCollection;
          return this.utils.secsToDuration(dataOrCollection / 1000, false, 'mm');
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getTotalWorkTimeFromEvents(asset, 1) : 0;
      }
    },
    {
      id: 'totalWorkTimeAvailable',
      description: this.translate.instant('WORK_SCHEDULE.Total_Work_Time_Available'),
      label: this.translate.instant('WORK_SCHEDULE.Total_Work_Time_Available'),
      units: this.translate.instant('WORK_SCHEDULE.Total_Work_Time_Available'),
      cellType: 'string',
      showWhen: () => this.subscriberService.usesFeature(Feature.WORK_SCHEDULE),
      useRenderFunc: true,
      isDuration: true,
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          if (type === 'sort' || type === 'type') return dataOrCollection;
          return this.utils.secsToDuration(dataOrCollection / 1000, false, 'mm');
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getTotalWorkTimeFromEvents(asset, 2) : 0;
      }
    },
    {
      id: 'averageWorkTimeUnavailable',
      description: this.translate.instant('WORK_SCHEDULE.Average_Work_Time_Unavailable'),
      label: this.translate.instant('WORK_SCHEDULE.Average_Work_Time_Unavailable'),
      units: this.translate.instant('WORK_SCHEDULE.Average_Work_Time_Unavailable'),
      cellType: 'string',
      showWhen: () => this.subscriberService.usesFeature(Feature.WORK_SCHEDULE),
      useRenderFunc: true,
      isDuration: true,
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          if (type === 'sort' || type === 'type') return dataOrCollection;
          return this.utils.secsToDuration(dataOrCollection / 1000, false, 'mm');
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getAverageWorkTimeFromEvents(asset, 0) : 0;
      }
    },
    {
      id: 'averageWorkTimeStrained',
      description: this.translate.instant('WORK_SCHEDULE.Average_Work_Time_Strained'),
      label: this.translate.instant('WORK_SCHEDULE.Average_Work_Time_Strained'),
      units: this.translate.instant('WORK_SCHEDULE.Average_Work_Time_Strained'),
      cellType: 'string',
      showWhen: () => this.subscriberService.usesFeature(Feature.WORK_SCHEDULE),
      useRenderFunc: true,
      isDuration: true,
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          if (type === 'sort' || type === 'type') return dataOrCollection;
          return this.utils.secsToDuration(dataOrCollection / 1000, false, 'mm');
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getAverageWorkTimeFromEvents(asset, 1) : 0;
      }
    },
    {
      id: 'averageWorkTimeAvailable',
      description: this.translate.instant('WORK_SCHEDULE.Average_Work_Time_Available'),
      label: this.translate.instant('WORK_SCHEDULE.Average_Work_Time_Available'),
      units: this.translate.instant('WORK_SCHEDULE.Average_Work_Time_Available'),
      cellType: 'string',
      showWhen: () => this.subscriberService.usesFeature(Feature.WORK_SCHEDULE),
      useRenderFunc: true,
      isDuration: true,
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          if (type === 'sort' || type === 'type') return dataOrCollection;
          return this.utils.secsToDuration(dataOrCollection / 1000, false, 'mm');
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getAverageWorkTimeFromEvents(asset, 2) : 0;
      }
    },
    // Percentage clock time columns
    {
      id: 'percentTimeUnavailable',
      description: this.translate.instant('PROPERTY.Percent_Time_Unavailable'),
      label: this.translate.instant('PROPERTY.Percent_Time_Unavailable'),
      units: '%',
      cellType: 'number',
      showWhen: true,
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          return dataOrCollection;
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getPercentHealthTime(asset, 0) : 0;
      }
    },
    {
      id: 'percentTimeStrained',
      description: this.translate.instant('PROPERTY.Percent_Time_Strained'),
      label: this.translate.instant('PROPERTY.Percent_Time_Strained'),
      units: '%',
      cellType: 'number',
      showWhen: true,
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          return dataOrCollection;
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getPercentHealthTime(asset, 1) : 0;
      }
    },
    {
      id: 'percentTimeAvailable',
      description: this.translate.instant('PROPERTY.Percent_Time_Available'),
      label: this.translate.instant('PROPERTY.Percent_Time_Available'),
      units: '%',
      cellType: 'number',
      showWhen: true,
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          return dataOrCollection;
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getPercentHealthTime(asset, 2) : 0;
      }
    },
    // Percentage work time columns (conditional on Work Schedule module)
    {
      id: 'percentWorkTimeUnavailable',
      description: this.translate.instant('WORK_SCHEDULE.Percent_Work_Time_Unavailable'),
      label: this.translate.instant('WORK_SCHEDULE.Percent_Work_Time_Unavailable'),
      units: '%',
      cellType: 'number',
      showWhen: () => this.subscriberService.usesFeature(Feature.WORK_SCHEDULE),
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          return dataOrCollection;
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getPercentWorkTime(asset, 0) : 0;
      }
    },
    {
      id: 'percentWorkTimeStrained',
      description: this.translate.instant('WORK_SCHEDULE.Percent_Work_Time_Strained'),
      label: this.translate.instant('WORK_SCHEDULE.Percent_Work_Time_Strained'),
      units: '%',
      cellType: 'number',
      showWhen: () => this.subscriberService.usesFeature(Feature.WORK_SCHEDULE),
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          return dataOrCollection;
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getPercentWorkTime(asset, 1) : 0;
      }
    },
    {
      id: 'percentWorkTimeAvailable',
      description: this.translate.instant('WORK_SCHEDULE.Percent_Work_Time_Available'),
      label: this.translate.instant('WORK_SCHEDULE.Percent_Work_Time_Available'),
      units: '%',
      cellType: 'number',
      showWhen: () => this.subscriberService.usesFeature(Feature.WORK_SCHEDULE),
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection === 'number') {
          return dataOrCollection;
        }
        const asset = dataOrCollection?.[0];
        return asset ? this.getPercentWorkTime(asset, 2) : 0;
      }
    },
    // CVX-6485: asset:inUse time, summed over every asset in the row and every inUse property
    this.inUseColumn('totalTimeInUse', 'PROPERTY.Total_Time_In_Use',
      (collection) => this.sumInUseTime(collection, 1), { duration: true }),
    this.inUseColumn('totalTimeNotInUse', 'PROPERTY.Total_Time_Not_In_Use',
      (collection) => this.sumInUseTime(collection, 0), { duration: true }),
    this.inUseColumn('percentTimeInUse', 'PROPERTY.Percent_Time_In_Use',
      (collection) => this.percentInUse(this.sumInUseTime(collection, 1), this.sumInUseTime(collection, 0))),
    this.inUseColumn('totalWorkTimeInUse', 'WORK_SCHEDULE.Total_Work_Time_In_Use',
      (collection) => this.sumInUseWorkTime(collection, 1), { duration: true, work: true }),
    this.inUseColumn('totalWorkTimeNotInUse', 'WORK_SCHEDULE.Total_Work_Time_Not_In_Use',
      (collection) => this.sumInUseWorkTime(collection, 0), { duration: true, work: true }),
    this.inUseColumn('percentWorkTimeInUse', 'WORK_SCHEDULE.Percent_Work_Time_In_Use',
      (collection) => this.percentInUse(this.sumInUseWorkTime(collection, 1), this.sumInUseWorkTime(collection, 0)), { work: true })
  ];
  private fieldOptions = [
    {
      id: 'assetID',
      description: this.translate.instant('MGMT_LIST.Asset'),
      label: this.translate.instant('MGMT_LIST.Asset'),
      fieldName: 'assetID',
      fieldType: 'number',
      fieldFunc: (assetID: number) => {
        let assetName = this.assetService.getAssetById(assetID)?.[0]?.name;

        if (!assetName) {
          assetID = 0;
          assetName = this.translate.instant('DASHPAGES.NA');
        }

        return [assetID, assetName];
      }
    },
    {
      id: 'assetFolder',
      description: this.translate.instant('MGMT_DETAILS.Asset_Folder'),
      label: this.translate.instant('MGMT_DETAILS.Asset_Folder'),
      fieldName: 'assetID',
      fieldType: 'number',
      fieldFunc: (assetID: number) => {
        const folderID = this.assetService.getAssetById(assetID)?.[0]?.folderID;
        const folderName = this.foldersDataService.getFolderByID(folderID, false)?.title;

        return [folderID || 0, folderName || this.translate.instant('DASHPAGES.NA')];
      }
    },
    // { TODO these fields will be moved to a new content report
    //   id: 'content',
    //   description: this.translate.instant('LAYOUT.Content'),
    //   label: this.translate.instant('LAYOUT.Content'),
    //   fieldName: 'contentItems',
    //   fieldType: 'array',
    //   fieldFunc: (contentID: number) => {
    //     const contentName = this.objectsService.getCachedObjectById(contentID)?.description;
    //     return [contentID, contentName || this.translate.instant('DASHPAGES.NA')];
    //   }
    // },
    // {
    //   id: 'contentFolder',
    //   description: this.translate.instant('CONTENT.Content_Folder'),
    //   label: this.translate.instant('CONTENT.Content_Folder'),
    //   fieldName: 'contentItems',
    //   fieldType: 'array',
    //   fieldFunc: (contentID: number) => {
    //     const contentObject = this.objectsService.getCachedObjectById(contentID);
    //
    //     if (contentObject?.folderID) {
    //       const folderName = this.foldersDataService.getFolderByID(contentObject.folderID, false)?.title
    //
    //       if (folderName) {
    //         return [contentObject.folderID, folderName];
    //       }
    //     }
    //     return [0, this.translate.instant('DASHPAGES.NA')];
    //   }
    // },
    {
      id: 'property',
      description: this.translate.instant('SHARED.Property'),
      label: this.translate.instant('SHARED.Property'),
      fieldName: 'properties',
      fieldType: 'array',
      fieldFunc: (property: IPropertyMeta) => {
        const propertyName = this.propertyService.getPropertyById(property?.propertyID)?.title;
        return [property?.propertyID || 0, propertyName || this.translate.instant('DASHPAGES.NA')];
      }
    },
    {
      id: 'propertyFolder',
      description: this.translate.instant('PROPERTY.Property_Folder'),
      label: this.translate.instant('PROPERTY.Property_Folder'),
      fieldName: 'properties',
      fieldType: 'array',
      fieldFunc: (property: IPropertyMeta) => {
        const folderID = this.propertyService.getPropertyById(property?.propertyID)?.folderID;
        const folder = this.foldersDataService.getFolderByID(folderID);
        return [folder || 0, folder?.title || this.translate.instant('DASHPAGES.NA')];
      }
    },
    {
      id: 'location',
      description: this.translate.instant('SHARED.Location'),
      label: this.translate.instant('SHARED.Location'),
      fieldName: 'location',
      fieldType: 'number',
      fieldFunc: (locationId: number) => {
        let locationName: string = get(this.userService.findLocation(locationId), 'name');

        if (!locationName) {
          locationId = 0;
          locationName = this.translate.instant('DASHPAGES.NA');
        }
        return [locationId, locationName];
      }
    },
    {
      id: 'zone',
      description: this.translate.instant('SHARED.Zone'),
      label: this.translate.instant('SHARED.Zone'),
      fieldName: 'zone',
      fieldType: 'integer',
      fieldFunc: (zone: number, asset: Asset) => {
        const zsig = asset.location + ':' + asset.zone;
        const locRef = this.userService.findLocation(asset.location);
        const zoneRef = this.userService.findAnyZone(locRef, asset.zone);
        if (zoneRef) {
          return [zsig, `${locRef.name}: ${zoneRef.name}`];
        } else {
          if (locRef) {
            return [zsig, locRef.name + '/Site-wide'];
          } else {
            return [zsig, 'Site-wide'];
          }
        }
      }
    },
    {
      id: 'resource',
      label: this.translate.instant('MGMT_DETAILS.Go_Tos'),
      description: this.translate.instant('MGMT_DETAILS.Go_Tos'),
      fieldName: 'users',
      fieldType: 'array',
      fieldFunc: (userID: number) => {
        return [userID, this.userService.getFullname(userID)];
      }
    },
    {
      id: 'health',
      label: this.translate.instant('SHARED.Status'),
      description: this.translate.instant('SHARED.Status'),
      fieldName: 'health',
      fieldType: 'integer',
      fieldFunc: (health: number) => {
        return [health, this.assetService.assetState(health)];
      }
    }
  ];

  private tableColumns = {
    observation: [
      {
        id: 'observation',
        label: this.translate.instant('SHARED.Response_ID'),
        func: (observation: Observation) => {
          return observation?.observationID || this.translate.instant('SHARED.None');
        }
      },
      {
        id: 'observation',
        label: this.translate.instant('SHARED.Type'),
        func: (observation: Observation) => {
          return this.observationService.getTitle(observation.type as ObservationTypes);
        }
      },
      {
        id: 'observation',
        label: this.translate.instant('SHARED.SUBTYPE'),
        func: (observation: Observation) => {
          if (observation.subtype) {
            return this.observationService.getTitle(observation.type as ObservationTypes, observation.subtype);
          } else {
            return this.translate.instant('SHARED.None');
          }
        }
      },
      {
        id: 'observation',
        label: this.translate.instant('REPORTING.CREATOR'),
        func: (observation: Observation) => {
          return this.accountService.fullname(observation.userID);
        }
      },
      {
        id: 'observation',
        label: this.translate.instant('REPORTING.CREATED'),
        func: (observation: Observation) => {
          return this.utils.dateTimeFormat(observation.created, null, true);
        }
      },
      {
        id: 'observation',
        label: this.translate.instant('SHARED.OWNER'),
        func: (observation: Observation) => {
          return this.accountService.fullname(observation.ownerID);
        }
      },
      {
        id: 'observation',
        label: this.translate.instant('SHARED.TEAM'),
        func: (observation: Observation) => {
          return this.teamsService.teamNameByID(observation.groupID);
        }
      }
    ],
    propertyValue: [
      {
        id: 'propertyValue',
        label: this.translate.instant('SHARED.USER'),
        func: (propertyValue: AssetByPropertyValue) => {
          return this.accountService.fullname(propertyValue?.value?.userID);
        }
      },
      {
        id: 'propertyValue',
        label: this.translate.instant('MGMT_DETAILS.Log_Activity'),
        func: (propertyValue: AssetByPropertyValue) => {
          return this.propertyService.getPropertyActivity(propertyValue?.value?.activity);
        }
      },
      {
        id: 'propertyValue',
        label: this.translate.instant('PROPERTY.Property_value'),
        func: (propertyValue: AssetByPropertyValue) => {
          return this.propertyService.displayPropertyValue(propertyValue.id, this.propertyService.getPropertyValue(propertyValue.value, propertyValue.id));
        }
      }
    ],
    totalTime: [
      {
        id: 'healthEvent',
        label: this.translate.instant('SHARED.When'),
        func: (event: HealthEvent) => {
          return this.utils.dateTimeFormat(event.time, null, true);
        }
      },
      {
        id: 'healthEvent',
        label: this.translate.instant('PROPERTY.Event_Data'),
        func: (event: HealthEvent) => {
          return join(map(omit(event, 'time'), (value, property) => {
            return `${property}: ${value}`;
          }), ', ');
        }
      }
    ]
  }

  constructor(
    private translate: TranslateService,
    private userService: UserService,
    private assetService: AssetsService,
    private utils: UtilsService,
    private foldersDataService: FoldersDataService,
    private objectsService: ObjectsService,
    private propertyService: PropertyService,
    private observationService: ObservationService,
    private accountService: AccountsService,
    private teamsService: TeamsService,
    private subscriberService: SubscriberService,
    private compareValueService: CompareValueService,
    private timeDisplayService: TimeDisplayService,
    private workScheduleService: WorkScheduleService
  ) {}

  public getColumns() {
    return [
      ...filter(cloneDeep(this.columnOptions), (opt) => {
        return typeof opt.showWhen === 'function' ? opt.showWhen() : opt.showWhen;
      }),
      ...this.getDynamicNumericPropertyFields()
    ];
  }

  public getFieldOptions() {
    return this.fieldOptions;
  }

  public outOfRange(opts, asset: Asset, theTime: number, interval): boolean {
    const testTime = this.utils.toMilliseconds(theTime);
    let start = this.utils.toMilliseconds(interval?.startTime ?? interval?.start);
    let end = this.utils.toMilliseconds(interval?.endTime ?? interval?.end);
    if (!start || !end) {
      return false;
    }
    if (opts?.locationTime) {
      if (asset.location) {
        const locRef = this.userService.getLocation(asset.location);
        if (locRef?.timezone) {
          if (!interval?.timezones) {
            interval.timezones = {};
          }

          if (!interval.timezones[locRef.timezone]) {
            const delta = this.utils.tzDelta(locRef.timezone, start);
            interval.timezones[locRef.timezone] = {
              start: start - delta,
              end: end - delta
            };
          }
          start = interval.timezones[locRef.timezone].start;
          end = interval.timezones[locRef.timezone].end;
        }
      }
    }

    return testTime < start || testTime > end;
  }

  public isValid(asset: Asset, selectors): any {
    // location filter
    if (selectors.locations && selectors.locations.length) {
      if (!includes(selectors.locations, asset.location)) {
        return false;
      } else {
        let zonesIds: any = [0];

        each(selectors.locations, (locationId: number) => {
          const location: any = find(this.userService.locations.data, <any>{locationID: locationId});
          const currentZonesIds: any = [];
          each(location.zones, zone => {
            if (!zone.disabledAt) {
              currentZonesIds.push(zone.zoneID);
            }
            // now look for subzones within these zones
            each(zone.zones, subZone => {
              if (!subZone.disabledAt) {
                currentZonesIds.push(subZone.zoneID);
              }
            });
          });
          zonesIds = [...zonesIds, ...currentZonesIds];
        });

        if (!includes(zonesIds, asset.zone)) {
          return false;
        }
      }
    }

    // zone filter
    if (selectors.zones && selectors.zones.length) {
      const zoneId = asset.zone ? +asset.zone : `${asset.location}:${asset.zone}`;
      const zoneIds = map(selectors.zones, (zoneId: string | number) => {
        return isNaN(+zoneId) ? zoneId : +zoneId;
      });
      if (!includes(zoneIds, zoneId)) {
        return false;
      }
    }

    if (selectors?.resources?.length && asset?.users?.length) {
      if (intersection(selectors.resources, map(asset.users, Number)).length === 0) {
        return false;
      }
    }

    if (has(selectors, 'active')) {
      if (asset.active !== selectors.active) {
        return false
      }
    }

    if (selectors?.targetAssets?.length) {
      const isMatch = some(selectors?.targetAssets, (assetFolder) => {
        if (assetFolder.assetID) {
          return assetFolder.assetID === +asset.assetID;
        } else {
          return this.foldersDataService.hasIn(assetFolder.folderID, asset?.folderID, selectors.assetFolderTree);
        }
      });

      if (!isMatch) {
        return false;
      }
    }

    if (selectors?.targetContent?.length) {
      const folderIDs = filter(map(asset?.contentItems, (contentID) => {
        return this.objectsService.getCachedObjectById(+contentID)?.folderID;
      }));
      const isMatch = some(selectors?.targetContent, (contentFolder) => {
        if (contentFolder.itemID) {
          return includes(asset.contentItems, contentFolder.itemID);
        } else {
          return includes(folderIDs, contentFolder.folderID);
        }
      });

      if (!isMatch) {
        return false;
      }
    }

    if (selectors.statusValue?.length && has(asset, 'health')) {
      if (!includes(selectors.statusValue, asset.health)) {
        return false;
      }
    }

    return true;
  }

  public getAssetColumns(reportOptions: IObjectStringKeyMap<any>) {
    const columns = [];
    const fields = this.getFieldOptions();

    columns.push(find(fields, { id: reportOptions?.primary }));
    columns.push(find(fields, { id: reportOptions?.secondary }));

    // CVX-6546: a chart click passes the clicked column; a stored list (no click) falls back to the first
    const graphColumn = flatten([reportOptions.graphSecondary])[0];
    if (this.isObservationGraph(graphColumn)) {
      columns.push(...this.tableColumns.observation);
    } else if (this.isTotalTimeGraph(graphColumn)) {
      columns.push(...this.tableColumns.totalTime);
    }

    return map(filter(columns), (column) => {
      column.headerClass = 'table-header';

      if (!column.title && column.label) {
        column.title = column.label;
      }

      if (!column.func && column.fieldFunc) {
        column.func = (id, data) => column.fieldFunc(id || data[column.fieldName], data)?.[1]
      }

      return column;
    });
  }

  public getTableAssetDataByGraphType(assets: Asset[], options: IObjectStringKeyMap<any>) {
    const type = flatten([options?.graphSecondary])[0];

    if (this.isObservationGraph(type)) {
      let assetsByObservation: AssetByObservation[] = [];
      const observationTypeMap = {
        observationsAdded: 'created',
        observationsFixed: 'fixed',
        observationsClosed: 'resolved'
      };

      each(assets, (asset) => {
        const observations = filter(map(filter(asset.observationEvents, { activity: observationTypeMap[type] }), (event) => {
          return this.observationService.getObservationById(event.observationID) as Observation;
        }));

        each(observations, (observation) => {
          assetsByObservation.push(Object.assign({}, asset, { observation }));
        });
      });

      return assetsByObservation;
    } else if(this.isTotalTimeGraph(type)) {
      let assetsByEvent: AssetByHealthEvent[] = [];
      const inUse = includes(AssetSummaryModelService.inUseTimeGraphs, type);

      each(assets, (asset) => {
        each(inUse ? asset.stateEvents : asset.healthEvents, (healthEvents: (HealthEvent | StateEvent)[]) => {
          each(healthEvents, (healthEvent) => {
            const eventTime = healthEvent.time * 1000;
            if (eventTime >= options.startTime && eventTime <= options.endTime) {
              assetsByEvent.push(Object.assign({}, asset, { healthEvent }));
            }
          });
        });
      });

      return assetsByEvent;
    }

    return assets;
  }

  public isObservationGraph(type: string) {
    return includes(['observationsAdded', 'observationsFixed', 'observationsClosed'], type);
  }

  public isTotalTimeGraph(type: string) {
    return includes([
      'totalTimeAvailable',
      'totalTimeUnavailable',
      'totalTimeStrained',
      'averageTimeUnavailable',
      'averageTimeStrained',
      'averageTimeAvailable',
      ...AssetSummaryModelService.inUseTimeGraphs
    ], type);
  }

  private static readonly inUseTimeGraphs = ['totalTimeInUse', 'totalTimeNotInUse'];

  /**
   * Checks if an asset passes all time-based filters.
   * Time filters compare calculated health duration values against user-specified thresholds.
   */
  public passesTimeFilters(asset: ReportAsset, opts: IObjectStringKeyMap<any>): boolean {
    // Clock time filters (total time in each health state)
    if (opts.filterTimeUnavailable) {
      const value = this.getTotalHealthTime(asset.healthEntities, 0);
      if (!this.matchesDurationFilter(value, opts.filterTimeUnavailable)) {
        return false;
      }
    }

    if (opts.filterTimeStrained) {
      const value = this.getTotalHealthTime(asset.healthEntities, 1);
      if (!this.matchesDurationFilter(value, opts.filterTimeStrained)) {
        return false;
      }
    }

    if (opts.filterTimeAvailable) {
      const value = this.getTotalHealthTime(asset.healthEntities, 2);
      if (!this.matchesDurationFilter(value, opts.filterTimeAvailable)) {
        return false;
      }
    }

    // Work time filters (only if Work Schedule module is enabled)
    if (this.subscriberService.usesFeature(Feature.WORK_SCHEDULE)) {
      if (opts.filterWorkTimeUnavailable) {
        const value = this.getTotalWorkTimeFromEvents(asset, 0);
        if (!this.matchesDurationFilter(value, opts.filterWorkTimeUnavailable)) {
          return false;
        }
      }

      if (opts.filterWorkTimeStrained) {
        const value = this.getTotalWorkTimeFromEvents(asset, 1);
        if (!this.matchesDurationFilter(value, opts.filterWorkTimeStrained)) {
          return false;
        }
      }

      if (opts.filterWorkTimeAvailable) {
        const value = this.getTotalWorkTimeFromEvents(asset, 2);
        if (!this.matchesDurationFilter(value, opts.filterWorkTimeAvailable)) {
          return false;
        }
      }
    }

    // Percentage clock time filters
    if (opts.filterPercentTimeUnavailable) {
      const value = this.getPercentHealthTime(asset, 0);
      if (!this.compareValueService.matches(value, opts.filterPercentTimeUnavailable)) {
        return false;
      }
    }

    if (opts.filterPercentTimeStrained) {
      const value = this.getPercentHealthTime(asset, 1);
      if (!this.compareValueService.matches(value, opts.filterPercentTimeStrained)) {
        return false;
      }
    }

    if (opts.filterPercentTimeAvailable) {
      const value = this.getPercentHealthTime(asset, 2);
      if (!this.compareValueService.matches(value, opts.filterPercentTimeAvailable)) {
        return false;
      }
    }

    // Percentage work time filters (only if Work Schedule module is enabled)
    if (this.subscriberService.usesFeature(Feature.WORK_SCHEDULE)) {
      if (opts.filterPercentWorkTimeUnavailable) {
        const value = this.getPercentWorkTime(asset, 0);
        if (!this.compareValueService.matches(value, opts.filterPercentWorkTimeUnavailable)) {
          return false;
        }
      }

      if (opts.filterPercentWorkTimeStrained) {
        const value = this.getPercentWorkTime(asset, 1);
        if (!this.compareValueService.matches(value, opts.filterPercentWorkTimeStrained)) {
          return false;
        }
      }

      if (opts.filterPercentWorkTimeAvailable) {
        const value = this.getPercentWorkTime(asset, 2);
        if (!this.compareValueService.matches(value, opts.filterPercentWorkTimeAvailable)) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Matches a duration value against a CompareDurationValue filter.
   * Uses CompareValueService for comparison logic since durations are stored as milliseconds.
   */
  private matchesDurationFilter(value: number, filterValue: CompareDurationValue): boolean {
    if (!filterValue?.id) {
      return true;
    }
    // Use the existing compareValueService since CompareDurationValue has the same structure
    return this.compareValueService.matches(value, filterValue as any);
  }

  private getHealthTime(healthEntities: AssetStateHistoryEntity[], health: number): { total: number; periodCount: number } {
    let durationSeconds = 0;
    let periodCount = 0;

    each(healthEntities, (healthEntity) => {
      each(healthEntity.stateHistoryPeriods, (stateHistoryPeriod) => {
        const periods = filter(flatten([stateHistoryPeriod.period]));
        // Use filter to get ALL periods with matching health, not just the first one
        const targetPeriods = filter(periods, { health });

        each(targetPeriods, (targetPeriod) => {
          if (targetPeriod?.value) {
            durationSeconds += targetPeriod.value;
            periodCount++;
          }
        });
      });
    });

    // Convert seconds to milliseconds for consistency with filter values
    return { total: durationSeconds * 1000, periodCount };
  }

  private getTotalHealthTime(healthEntities: AssetStateHistoryEntity[], health: number) {
    return this.getHealthTime(healthEntities, health).total;
  }

  private getAverageHealthTime(healthEntities: AssetStateHistoryEntity[], health: number) {
    const { total, periodCount } = this.getHealthTime(healthEntities, health);
    if (periodCount === 0) return 0;
    return +(total / periodCount).toFixed(1);
  }

  /**
   * Clear the work time cache. Call this when starting a new report calculation.
   */
  public clearWorkTimeCache(): void {
    this.workTimeCache.clear();
  }

  /**
   * Get work time for a specific health state from health events.
   *
   * Logic:
   * - Trust workTimeSeconds when event period is fully within report range
   * - Calculate work time using TimeDisplayService when:
   *   a) Previous event is BEFORE report start (need partial period from reportStart to eventTime)
   *   b) Current event is AFTER report end (need partial period from prevEventTime to reportEnd)
   * - Fall back to clock time when no work schedule exists
   */
  private getWorkTimeFromEvents(
    asset: ReportAsset,
    targetHealth: number
  ): { total: number; periodCount: number } {
    const healthEvents = asset.healthEvents;

    // Collect all events from propertyID 0 (overall health) or use first property
    // PropertyID 0 represents the overall asset health composite
    let events: HealthEvent[] = healthEvents?.['0'] || [];
    if (!events.length) {
      // Fall back to first available property's events
      const firstKey = Object.keys(healthEvents || {})[0];
      events = firstKey ? healthEvents[firstKey] : [];
    }

    return this.getWorkTime([events], asset.healthEntities, asset.location || 0, `health_${asset.assetID}`, targetHealth);
  }

  /**
   * asset:inUse work time (state 1 = in use, 0 = not in use). Each inUse property is its own
   * stream - summed, never interleaved, which would make segments out of two properties' changes.
   */
  private getInUseWorkTime(asset: ReportAsset, targetState: number): { total: number; periodCount: number } {
    const propertyIDs = filter(Object.keys(asset.stateEvents || {}), (propertyID) => this.propertyService.getPropertyById(+propertyID)?.disabledAt === 0);
    const streams = values(stateEventsAsHealthEvents(pick(asset.stateEvents, propertyIDs)));

    return this.getWorkTime(streams, asset.stateEntities, asset.location || 0, `inUse_${asset.assetID}_${propertyIDs.join('-')}`, targetState);
  }

  /**
   * Work time in one state, summed over independent event streams (health: one stream; inUse:
   * one per property). The range, the cache and the no-schedule shortcut apply to the whole sum.
   */
  private getWorkTime(
    streams: HealthEvent[][],
    entities: AssetStateHistoryEntity[],
    locationID: number,
    cacheKeyPrefix: string,
    targetState: number
  ): { total: number; periodCount: number } {
    // Get report range from the entities
    const reportRange = this.getReportRange(entities);
    if (!reportRange) {
      // Fall back to clock time if no range info available
      return this.getHealthTime(entities, targetState);
    }

    const { startTime: reportStartMs, endTime: reportEndMs } = reportRange;

    // Check cache (the prefix keeps health and inUse - whose states share numbers - apart)
    const cacheKey = `${cacheKeyPrefix}_${targetState}_${reportStartMs}_${reportEndMs}`;
    const cached = this.workTimeCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    // Check if work schedules exist for this location
    // If no schedules are defined, work time equals clock time
    const hasWorkSchedules = this.locationHasWorkSchedules(locationID);
    if (!hasWorkSchedules) {
      // No work schedules - work time equals clock time
      const clockTimeResult = this.getHealthTime(entities, targetState);
      this.workTimeCache.set(cacheKey, clockTimeResult);
      return clockTimeResult;
    }

    const withEvents = filter(streams, (events) => events?.length > 0);
    if (!withEvents.length) {
      // No events - fall back to clock time
      return this.getHealthTime(entities, targetState);
    }

    let totalWorkTimeSeconds = 0;
    let periodCount = 0;
    each(withEvents, (events) => {
      const streamResult = this.getStreamWorkTime(events, reportStartMs, reportEndMs, locationID, targetState);
      totalWorkTimeSeconds += streamResult.seconds;
      periodCount += streamResult.periodCount;
    });

    // Convert to milliseconds for consistency with clock time values
    const result = { total: totalWorkTimeSeconds * 1000, periodCount };

    // Cache only what the schedule fully answered: a clock-time fallback (schedule still loading)
    // or a still-open state (it ends now) would otherwise be served stale on the next report run
    const settled = reportEndMs <= Date.now() &&
      this.timeDisplayService.isScheduleCachedSync(Math.floor(reportStartMs / 1000), Math.floor(reportEndMs / 1000), locationID);
    if (settled) {
      this.workTimeCache.set(cacheKey, result);
    }

    return result;
  }

  /** Work seconds spent in targetState by one event stream, clipped to the report range. */
  private getStreamWorkTime(
    events: HealthEvent[],
    reportStartMs: number,
    reportEndMs: number,
    locationID: number,
    targetHealth: number
  ): { seconds: number; periodCount: number } {
    // Sort events by time
    const sortedEvents = [...events].sort((a, b) => a.time - b.time);

    // Calculate work time for each inter-event period matching targetHealth
    let totalWorkTimeSeconds = 0;
    let periodCount = 0;

    for (let i = 0; i < sortedEvents.length; i++) {
      const event = sortedEvents[i];
      const prevEvent = sortedEvents[i - 1];
      const eventTimeMs = event.time * 1000;

      // Skip events entirely outside report range
      if (eventTimeMs <= reportStartMs && i < sortedEvents.length - 1) {
        continue;
      }

      // Determine the health state for the period BEFORE this event
      // (either from prevEvent or from this event if it's the first one in range)
      let periodHealth: number;
      let periodStartMs: number;
      let periodEndMs: number;

      if (prevEvent) {
        // This event marks the END of prevEvent's health state period
        periodHealth = prevEvent.health;
        periodStartMs = prevEvent.time * 1000;
        periodEndMs = eventTimeMs;
      } else {
        // First event - skip (we don't know the health state before it)
        continue;
      }

      // Only process if this period matches the target health
      if (periodHealth !== targetHealth) {
        continue;
      }

      // Clamp period to report range
      const clampedStartMs = Math.max(periodStartMs, reportStartMs);
      const clampedEndMs = Math.min(periodEndMs, reportEndMs);

      if (clampedEndMs <= clampedStartMs) {
        continue; // Period doesn't overlap with report range
      }

      // Determine if we can trust workTimeSeconds or need to calculate
      const periodFullyWithinRange = periodStartMs >= reportStartMs && periodEndMs <= reportEndMs;

      if (periodFullyWithinRange && event.workTimeSeconds !== undefined && event.workTimeSeconds > 0) {
        // Period is fully within report range - trust workTimeSeconds
        totalWorkTimeSeconds += event.workTimeSeconds;
      } else {
        // Period extends outside report range - work time for the clamped portion, from the
        // schedule (clock time with no location: a 24/7 operation)
        totalWorkTimeSeconds += this.timeDisplayService.calculateWorkTimeBetweenSync(
          Math.floor(clampedStartMs / 1000), Math.floor(clampedEndMs / 1000), locationID
        );
      }
      periodCount++;
    }

    // Handle the ongoing period (after the last event). It ends now, not at a range end that
    // is still in the future - the clock-time path stops there too.
    const lastEvent = sortedEvents[sortedEvents.length - 1];
    const lastEventTimeMs = lastEvent.time * 1000;
    const ongoingEndMs = Math.min(reportEndMs, Date.now());

    if (lastEventTimeMs < ongoingEndMs && lastEvent.health === targetHealth) {
      const ongoingStartMs = Math.max(lastEventTimeMs, reportStartMs);

      if (ongoingEndMs > ongoingStartMs) {
        totalWorkTimeSeconds += this.timeDisplayService.calculateWorkTimeBetweenSync(
          Math.floor(ongoingStartMs / 1000), Math.floor(ongoingEndMs / 1000), locationID
        );
        periodCount++;
      }
    }

    return { seconds: totalWorkTimeSeconds, periodCount };
  }

  /**
   * Check if work schedules are defined for a location.
   * Returns true if the location (or subscriber default) has work schedules defined.
   * Returns false if no schedules exist, meaning work time should equal clock time.
   */
  private locationHasWorkSchedules(locationID: number): boolean {
    // First, check if ANY schedules exist at all
    const allSchedules = this.workScheduleService.getWorkSchedulesSync();
    if (!allSchedules || allSchedules.length === 0) {
      return false;
    }

    if (!locationID || locationID <= 0) {
      // Check subscriber-level defaults
      const defaults = this.workScheduleService.getDefaultScheduleIDs();
      return defaults.workDaysScheduleID > 0 || defaults.daysOffScheduleID > 0;
    }

    // Check location-specific schedules
    const locationDefaults = this.workScheduleService.getLocationDefaultSchedules(locationID);
    return locationDefaults.workDaysScheduleID > 0 || locationDefaults.daysOffScheduleID > 0;
  }

  /**
   * Get the report date range from healthEntities
   */
  private getReportRange(healthEntities: AssetStateHistoryEntity[]): { startTime: number; endTime: number } | null {
    let startTime = Infinity;
    let endTime = 0;

    each(healthEntities, (entity) => {
      each(entity.stateHistoryPeriods, (period) => {
        if (period.range) {
          if (period.range.startTime < startTime) startTime = period.range.startTime;
          if (period.range.endTime > endTime) endTime = period.range.endTime;
        }
        // Also check nested period objects
        const periods = flatten([period.period]);
        each(periods, (p: any) => {
          if (p?.range) {
            if (p.range.startTime < startTime) startTime = p.range.startTime;
            if (p.range.endTime > endTime) endTime = p.range.endTime;
          }
        });
      });
    });

    if (startTime === Infinity || endTime === 0) {
      return null;
    }

    return { startTime, endTime };
  }

  private getTotalWorkTimeFromEvents(
    asset: ReportAsset,
    targetHealth: number
  ): number {
    return this.getWorkTimeFromEvents(asset, targetHealth).total;
  }

  private getAverageWorkTimeFromEvents(
    asset: ReportAsset,
    targetHealth: number
  ): number {
    const { total, periodCount } = this.getWorkTimeFromEvents(asset, targetHealth);
    if (periodCount === 0) return 0;
    return +(total / periodCount).toFixed(1);
  }

  /**
   * Calculate percentage of clock time in a health state.
   * Uses total period duration from healthEntities range.
   */
  private getPercentHealthTime(asset: ReportAsset, health: number): number {
    const totalTimeMs = this.getTotalHealthTime(asset.healthEntities, health);
    const periodDurationMs = this.getPeriodDuration(asset.healthEntities);

    if (!periodDurationMs || periodDurationMs <= 0) return 0;
    return Math.round((totalTimeMs / periodDurationMs) * 100 * 10) / 10; // 1 decimal place
  }

  /**
   * Get total period duration from healthEntities range metadata.
   */
  private getPeriodDuration(healthEntities: AssetStateHistoryEntity[]): number {
    let startTime = Infinity;
    let endTime = 0;

    each(healthEntities, (entity) => {
      each(entity.stateHistoryPeriods, (period) => {
        const periods = flatten([period.period]);
        each(periods, (p: any) => {
          if (p?.range) {
            if (p.range.startTime < startTime) startTime = p.range.startTime;
            if (p.range.endTime > endTime) endTime = p.range.endTime;
          }
        });
      });
    });

    return endTime > startTime ? endTime - startTime : 0;
  }

  /**
   * Calculate percentage of work time in a health state.
   */
  private getPercentWorkTime(asset: ReportAsset, health: number): number {
    const workTimeMs = this.getTotalWorkTimeFromEvents(asset, health);
    const totalWorkTimeMs = this.getTotalWorkTimeInPeriod(asset);

    if (!totalWorkTimeMs || totalWorkTimeMs <= 0) return 0;
    return Math.round((workTimeMs / totalWorkTimeMs) * 100 * 10) / 10; // 1 decimal place
  }

  /**
   * Get total work time in period (sum of all health states' work time).
   */
  private getTotalWorkTimeInPeriod(asset: ReportAsset): number {
    return this.getTotalWorkTimeFromEvents(asset, 0) +
           this.getTotalWorkTimeFromEvents(asset, 1) +
           this.getTotalWorkTimeFromEvents(asset, 2);
  }

  /** CVX-6485: clock ms in an inUse state (1 / 0), over every asset in the row (CVX-6283). */
  private sumInUseTime(collection: ReportAsset[], state: number): number {
    return sumBy(collection, (asset) => this.getTotalHealthTime(asset?.stateEntities, state)) || 0;
  }

  private sumInUseWorkTime(collection: ReportAsset[], state: number): number {
    return sumBy(collection, (asset) => asset ? this.getInUseWorkTime(asset, state).total : 0) || 0;
  }

  /** % of the tracked time spent in use: In Use / (In Use + Not In Use), not the range length. */
  private percentInUse(inUseMs: number, notInUseMs: number): number {
    const trackedMs = inUseMs + notInUseMs;
    return trackedMs > 0 ? Math.round((inUseMs / trackedMs) * 100 * 10) / 10 : 0;
  }

  /** A report column for asset:inUse time - a duration (clock or work time) or a percentage. */
  private inUseColumn(id: string, labelKey: string, value: (collection: ReportAsset[]) => number, options: { duration?: boolean; work?: boolean } = {}) {
    const label = this.translate.instant(labelKey);
    return {
      id,
      description: label,
      label,
      units: options.duration ? label : '%',
      cellType: options.duration ? 'string' : 'number',
      showWhen: options.work ? () => this.subscriberService.usesFeature(Feature.WORK_SCHEDULE) : true,
      ...(options.duration ? { useRenderFunc: true, isDuration: true } : {}),
      // data-building phase: the row's assets; render phase: the pre-calculated value (ms or %)
      func: (dataOrCollection: ReportAsset[] | number, row?: any, type?: string) => {
        if (typeof dataOrCollection !== 'number') {
          return value(dataOrCollection || []);
        }
        if (!options.duration || type === 'sort' || type === 'type') {
          return dataOrCollection;
        }
        return options.work
          ? this.utils.secsToDuration(dataOrCollection / 1000, false, 'mm')
          : this.timeDisplayService.formatDuration(dataOrCollection / 1000);
      }
    };
  }

  /**
   * CVX-6283: count matching observation events across EVERY asset in the group.
   * These columns previously read collection[0] only, so a folder row reported just
   * its first asset's activity - and a folder whose first asset had none rendered 0.
   *
   * Unlike the numeric property columns (CVX-6281), 0 is the right answer for an
   * empty group here: this is a count, and "no observations" genuinely is zero.
   */
  private countObservationEvents(collection: Asset[], activity: string): number {
    return sumBy(collection, (asset: Asset) => filter(values(asset?.observationEvents), { activity }).length) || 0;
  }

  private getDynamicNumericPropertyFields() {
    const properties = this.propertyService.getPropertiesByCategory(PropertyCategory.Numeric);

    // CVX-6554: one measure per property; property_<id> keeps meaning (and labelling) the average
    return flatMap(properties, (property) => expandMeasure({
      id: `property_${property.propertyID}`,
      label: property.title,
      treatments: ['avg', 'min', 'max', 'sum', 'count'],
      values: (collection: Asset[]) => this.getPropertyValues(collection, property),
      format: (value: number, treatment: Treatment) => treatment === 'count'
        ? value
        : +value.toFixed(this.propertyService.displayPrecision(property, 1)),
      legacy: {
        avg: {
          id: `property_${property.propertyID}`,
          description: `${property.title}: ${this.translate.instant('REPORTING.Average_Value')}`,
          label: `${property.title}: ${this.translate.instant('REPORTING.Average_Value')}`,
          units: `${property.title}: ${this.translate.instant('REPORTING.Average_Value')}`,
          cellType: 'number',
          showWhen: true,
          func: (collection: Asset[]) => this.getAverageValue(collection, property)
        }
      }
    }, (key) => this.translate.instant(key)));
  }

  /**
   * CVX-6281: average across EVERY asset in the group, not just the first one.
   * The values are pooled (one mean over all readings in the group) rather than
   * averaged per asset and then averaged again - a group's mean cycle time is the
   * mean of its readings, not the mean of its assets.
   *
   * Returns '' rather than 0 when the group has no readings: 0 is a legal reading
   * for these properties, so a missing feed must not render as a real zero.
   */
  private getAverageValue(collection: Asset[], property?: IProperty): number | string {
    const propertyValues = this.getPropertyValues(collection, property);

    if (!propertyValues.length) {
      return '';
    }

    return +(sumBy(propertyValues) / propertyValues.length).toFixed(this.propertyService.displayPrecision(property, 1));
  }

  /** Every numeric reading of the property across the group's assets, pooled. */
  private getPropertyValues(collection: Asset[], property?: IProperty): number[] {
    const propertyValues: number[] = [];

    each(collection, (asset: Asset) => {
      each(asset?.propertyEvents, (propertyEvents, propertyID) => {
        if (property && +propertyID !== property.propertyID) {
          return;
        }

        each(propertyEvents, (event) => {
          const eventValue = this.propertyService.getEventNumberValue(event, +propertyID);

          if (!isUndefined(eventValue)) {
            propertyValues.push(eventValue);
          }
        });
      });
    });

    return propertyValues;
  }
}
